// src/store/apiTokens/apiTokensSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiService from '../../services/api.service';

// Async thunks
export const fetchAPITokens = createAsyncThunk(
  'apiTokens/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.getAPITokens();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch API tokens');
    }
  }
);

export const createAPIToken = createAsyncThunk(
  'apiTokens/create',
  async (tokenData, { rejectWithValue }) => {
    try {
      const response = await apiService.createAPIToken(tokenData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to create API token');
    }
  }
);

export const deleteAPIToken = createAsyncThunk(
  'apiTokens/delete',
  async (tokenId, { rejectWithValue }) => {
    try {
      await apiService.deleteAPIToken(tokenId);
      return tokenId;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to delete API token');
    }
  }
);

export const toggleAPIToken = createAsyncThunk(
  'apiTokens/toggle',
  async (tokenId, { rejectWithValue }) => {
    try {
      const response = await apiService.toggleAPIToken(tokenId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to toggle API token');
    }
  }
);

export const activateAPIToken = createAsyncThunk(
  'apiTokens/activate',
  async (tokenId, { rejectWithValue }) => {
    try {
      const response = await apiService.activateAPIToken(tokenId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to activate API token');
    }
  }
);

export const deactivateAPIToken = createAsyncThunk(
  'apiTokens/deactivate',
  async (tokenId, { rejectWithValue }) => {
    try {
      const response = await apiService.deactivateAPIToken(tokenId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to deactivate API token');
    }
  }
);

const initialState = {
  tokens: [],
  loading: false,
  error: null,
  newToken: null, // Store newly created token (with raw token) temporarily
};

const apiTokensSlice = createSlice({
  name: 'apiTokens',
  initialState,
  reducers: {
    clearNewToken: (state) => {
      state.newToken = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all tokens
      .addCase(fetchAPITokens.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAPITokens.fulfilled, (state, action) => {
        state.loading = false;
        state.tokens = action.payload;
      })
      .addCase(fetchAPITokens.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Create token
      .addCase(createAPIToken.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.newToken = null;
      })
      .addCase(createAPIToken.fulfilled, (state, action) => {
        state.loading = false;
        // Store the new token with the raw token value
        state.newToken = action.payload;
        // Add to list (without the raw token)
        const { token, message, ...tokenWithoutRaw } = action.payload;
        state.tokens.unshift(tokenWithoutRaw);
      })
      .addCase(createAPIToken.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete token
      .addCase(deleteAPIToken.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteAPIToken.fulfilled, (state, action) => {
        state.loading = false;
        state.tokens = state.tokens.filter(token => token.id !== action.payload);
      })
      .addCase(deleteAPIToken.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Toggle token
      .addCase(toggleAPIToken.fulfilled, (state, action) => {
        const index = state.tokens.findIndex(token => token.id === action.payload.id);
        if (index !== -1) {
          state.tokens[index] = action.payload;
        }
      })

      // Activate token
      .addCase(activateAPIToken.fulfilled, (state, action) => {
        const index = state.tokens.findIndex(token => token.id === action.payload.id);
        if (index !== -1) {
          state.tokens[index] = action.payload;
        }
      })

      // Deactivate token
      .addCase(deactivateAPIToken.fulfilled, (state, action) => {
        const index = state.tokens.findIndex(token => token.id === action.payload.id);
        if (index !== -1) {
          state.tokens[index] = action.payload;
        }
      });
  },
});

export const { clearNewToken, clearError } = apiTokensSlice.actions;
export default apiTokensSlice.reducer;
